import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { PDFiumLibrary } from "@hyzyla/pdfium";
import wasmUrl from "@hyzyla/pdfium/pdfium.wasm?url";
import { PageSizes, PDFDocument } from "@cantoo/pdf-lib";
import { Canvas } from "fabric";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string): string {
  if (typeof date === "string") {
    date = new Date(date);
  }

  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

export function truncateString(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "...";
}

// TODO: optimize library initialization
export async function pdf2pngs(pdf: File, lowQuality: boolean = false): Promise<File[]> {
  if (pdf.type !== "application/pdf") {
    throw new Error("File is not a PDF");
  }
  const library = await PDFiumLibrary.init({
    wasmUrl,
  }); 
  const fileName = pdf.name.replace(/\.[^/.]+$/, '');

  const buffer = await pdf.arrayBuffer();
  const document = await library.loadDocument(new Uint8Array(buffer));

  const conversionPromises: Promise<File>[] = [];
  let pageIndex = 0;

  const promises = [];
  for (const page of document.pages()) {
    promises.push(page.render({
      render: "bitmap",
      scale: lowQuality ? 1 : 3, // maybe 5 ?
    }));
  }

  const images = await Promise.all(promises);

  for (const image of images) {
    const conversionPromise = convertBitmapToPngFile(
      image.data,
      image.width,
      image.height,
      `${fileName}_pagina_${pageIndex + 1}.png`
    );
    conversionPromises.push(conversionPromise);
    pageIndex++;
  }

  const resolvedImages = await Promise.all(conversionPromises);

  document.destroy();
  library.destroy();
  return resolvedImages;
}

async function convertBitmapToPngFile(
  bitmapData: ArrayBufferView,
  width: number,
  height: number,
  filename: string
): Promise<File> {

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Could not get 2D context from canvas");
  }

  const uint8Array = new Uint8Array(bitmapData.buffer, bitmapData.byteOffset, bitmapData.byteLength);
  const clampedArray = new Uint8ClampedArray(uint8Array);
  // Create ImageData using the correct overload signature
  const imageData = new ImageData(
    clampedArray,
    width,
    height
  );
  ctx.putImageData(imageData, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(new File([blob], filename, { type: "image/png" }));
      } else {
        reject(new Error("Canvas toBlob failed"));
      }
    }, "image/png");
  });
}

export async function mergePdfs(files: File[]): Promise<string> {
  // Create a new empty PDFDocument
  const mergedPdf = await PDFDocument.create();

  // Process all PDFs in parallel
  const pdfPromises = files.map(async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await PDFDocument.load(arrayBuffer, { password: "" });
    return mergedPdf.copyPages(pdf, pdf.getPageIndices());
  });

  // Wait for all PDFs to be processed
  const copiedPagesArrays = await Promise.all(pdfPromises);

  // Add all copied pages to the merged document
  copiedPagesArrays.flat().forEach((page) => mergedPdf.addPage(page));

  // Serialize the merged PDF to bytes
  const mergedPdfBytes = await mergedPdf.save();
  // Create a Blob and object URL for download
  const blob = new Blob([new Uint8Array(mergedPdfBytes)], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  return url 
}


export async function image2pdf(imageFiles: File[]): Promise<File[]> {
  const pdfPromises = imageFiles.map(async (imgFile) => {
    // If it's already a PDF, return it directly
    if (imgFile.type === 'application/pdf') {
      return imgFile;
    }

    const arrayBuffer = await imgFile.arrayBuffer();
    const pdfDoc = await PDFDocument.create();
    let embeddedImage;

    if (imgFile.type === 'image/png') {
      embeddedImage = await pdfDoc.embedPng(arrayBuffer);
    } else if (
      imgFile.type === 'image/jpeg' ||
      imgFile.type === 'image/jpg'
    ) {
      embeddedImage = await pdfDoc.embedJpg(arrayBuffer);
    } else {
      console.warn(
        `Skipping "${imgFile.name}", unsupported type "${imgFile.type}".`
      );
      return null;
    }

    const [width, height] = PageSizes.A4; 
    const {width: fitWidth, height: fitHeight} = embeddedImage.scaleToFit(width, height);
    const page = pdfDoc.addPage(PageSizes.A4);
    
    // Center the image on the page
    const x = (width - fitWidth) / 2;
    const y = (height - fitHeight) / 2;
    
    page.drawImage(embeddedImage, { x, y, width: fitWidth, height: fitHeight});

    const pdfBytes = await pdfDoc.save();
    const pdfName = imgFile.name.replace(/\.(png|jpe?g)$/i, '.pdf');
    const pdfFile = new File([new Uint8Array(pdfBytes)], pdfName, {
      type: 'application/pdf',
      lastModified: Date.now(),
    });

    return pdfFile;
  });

  // Wait for all PDFs to be processed and filter out null values
  const pdfFiles = (await Promise.all(pdfPromises)).filter((file): file is File => file !== null);

  return pdfFiles;
}

export async function canvas2png(canvas: Canvas, filename: string = 'canvas.png'): Promise<File> {
  return new Promise((resolve, reject) => {
    // Convert canvas to data URL (PNG format)
    const dataURL = canvas.toDataURL({
      format: 'png',
      quality: 1.0,
      multiplier: 2, // Higher resolution
    });

    // Convert data URL to blob
    fetch(dataURL)
      .then(res => res.blob())
      .then(blob => {
        // Create File from blob
        const file = new File([blob], filename, {
          type: 'image/png',
          lastModified: Date.now(),
        });
        resolve(file);
      })
      .catch(reject);
  });
}

export async function canvases2pdf(canvases: Canvas[], baseFilename: string = 'canvas'): Promise<string> {
  // Convert all canvases to PNG files
  const pngPromises = canvases.map((canvas, index) => {
    const filename = `${baseFilename}_${index + 1}.png`;
    return canvas2png(canvas, filename);
  });

  const pngFiles = await Promise.all(pngPromises);

  // Convert PNG files to individual PDF files
  const pdfFiles = await image2pdf(pngFiles);

  // Merge all PDFs into a single PDF and return the download URL
  const mergedPdfUrl = await mergePdfs(pdfFiles);

  return mergedPdfUrl;
}

